import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShieldCheck, Loader2 } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";

/**
 * /enrol/:slug — a nztaxation.nz-branded wrapper that embeds the matching
 * clientops.nz enrolment page inside an iframe. The visible URL stays on
 * nztaxation.nz, but the form itself is served by ClientOps.
 *
 * Slugs supported:
 *   - sole-trader  → https://clientops.nz/enrol/sole-trader
 *   - company      → https://clientops.nz/enrol/company (2-owner company)
 */
const PLANS = {
  "sole-trader": {
    title: "Sole Trader Enrolment",
    tagline: "Annual IR3 + GST returns, handled monthly.",
    src: "https://clientops.nz/enrol/sole-trader",
  },
  company: {
    title: "2-Owner Company Enrolment",
    tagline: "Company tax, GST and shareholder returns, sorted.",
    src: "https://clientops.nz/enrol/company",
  },
};

export default function EnrolFramePage() {
  const { slug } = useParams();
  const plan = PLANS[slug];
  const [loaded, setLoaded] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  if (!plan) {
    return (
      <main className="min-h-screen grid place-items-center bg-[#FAFAFA] px-6">
        <div className="text-center">
          <h1 className="font-heading font-black text-3xl">Enrolment path not found</h1>
          <Link
            to="/#pricing"
            className="mt-6 inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-3 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F]"
          >
            <ArrowLeft className="w-4 h-4" /> See plans
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid={`enrol-${slug}`}>
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Compact wrapper header */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-6 md:py-8 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            >
              <ArrowLeft className="w-3 h-3" /> nztaxation.nz
            </Link>
            <h1 className="mt-2 font-heading font-black text-2xl md:text-3xl tracking-tight">
              {plan.title}
            </h1>
            <p className="mt-1 text-slate-600 text-sm">{plan.tagline}</p>
          </div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 border border-slate-200 px-3 py-1.5 bg-slate-50">
            <ShieldCheck className="w-3 h-3 text-[#C9A349]" />
            Secure enrolment
          </div>
        </div>
      </section>

      {/* Payment-safe callout — always visible above the iframe.
          Stripe Checkout refuses to render inside a cross-origin iframe
          (X-Frame-Options: DENY), so if the user hits Pay while framed the
          Stripe redirect fails silently. This banner gives them a one-tap
          escape hatch to complete enrolment (including payment) in a proper
          top-level window where Stripe works normally. */}
      <section className="max-w-5xl mx-auto px-4 md:px-10 pt-4">
        <div
          className="border border-[#C9A349]/40 bg-[#C9A349]/10 px-4 md:px-5 py-3 flex flex-wrap items-center gap-3 justify-between text-sm"
          data-testid="enrol-payment-escape"
        >
          <div className="flex items-center gap-2 text-[#0A192F]">
            <ShieldCheck className="w-4 h-4 text-[#C9A349] flex-shrink-0" />
            <span>
              Ready to pay? Open the enrolment in a new window so your card
              screen loads properly.
            </span>
          </div>
          <a
            href={plan.src}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-4 py-2 font-heading font-bold uppercase tracking-[0.12em] text-xs hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors flex-shrink-0"
            data-testid="enrol-open-new-window"
          >
            Open payment window <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* Iframe */}
      <section className="max-w-5xl mx-auto px-4 md:px-10 py-6 md:py-8">
        <div className="relative bg-white border border-slate-200 shadow-sm overflow-hidden">
          {!loaded && (
            <div className="absolute inset-0 grid place-items-center bg-white/80 backdrop-blur-sm z-10">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#C9A349] mx-auto" />
                <div className="mt-3 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-500">
                  Loading secure form…
                </div>
              </div>
            </div>
          )}
          <iframe
            key={slug}
            src={plan.src}
            title={plan.title}
            onLoad={() => setLoaded(true)}
            className="w-full h-[calc(100vh-320px)] min-h-[720px] border-0 bg-white"
            data-testid={`enrol-${slug}-iframe`}
            allow="payment *; clipboard-write; clipboard-read"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        {/* Fallback: if iframe fails, open in new tab */}
        <div className="mt-4 text-center text-xs text-slate-500">
          Having trouble with the form?{" "}
          <a
            href={plan.src}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-heading font-bold text-[#0A192F] hover:text-[#C9A349]"
          >
            Open in a new window <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}
